import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import PollComponent from '../components/Poll';
import EmoticonPicker from '../components/EmoticonPicker';
import './DevChat.css';

interface DevChatMessage {
  id: number;
  user_id: number;
  username?: string;
  content: string;
  created_at: string;
}

interface PollOption {
  id: number;
  option_text: string;
  vote_count: number;
}

interface Poll {
  id: number;
  title: string;
  description?: string;
  created_by_username: string;
  created_at: string;
  ends_at?: string;
  vote_count: number;
  options: PollOption[];
}

const DevChat: React.FC = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState<DevChatMessage[]>([]);
  const [polls, setPolls] = useState<Poll[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);
  const [showPollForm, setShowPollForm] = useState(false);
  const [pollForm, setPollForm] = useState({
    title: '',
    description: '',
    options: ['', ''],
    ends_at: '',
  });
  const [creatingPoll, setCreatingPoll] = useState(false);
  const [showEmoticonPicker, setShowEmoticonPicker] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const [messagesResponse, pollsResponse] = await Promise.all([
          api.get('/dev-chat'),
          api.get('/polls'),
        ]);
        setMessages(messagesResponse.data.messages || []);
        setPolls(pollsResponse.data.polls || []);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Daten konnten nicht geladen werden');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handlePollVoteUpdate = async () => {
    try {
      const response = await api.get('/polls');
      setPolls(response.data.polls || []);
    } catch (error) {
      console.error('Fehler beim Aktualisieren der Umfragen:', error);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!content.trim()) return;
    setSending(true);
    setError('');
    try {
      const response = await api.post('/dev-chat', { content: content.trim() });
      const newMessage: DevChatMessage = {
        ...response.data.message,
        username: user?.username,
      };
      setMessages((prev) => [newMessage, ...prev]);
      setContent('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Nachricht konnte nicht gesendet werden');
    } finally {
      setSending(false);
    }
  };

  const handleAddPollOption = () => {
    if (pollForm.options.length < 10) {
      setPollForm({ ...pollForm, options: [...pollForm.options, ''] });
    }
  };

  const handleRemovePollOption = (index: number) => {
    if (pollForm.options.length > 2) {
      const newOptions = pollForm.options.filter((_, i) => i !== index);
      setPollForm({ ...pollForm, options: newOptions });
    }
  };

  const handlePollOptionChange = (index: number, value: string) => {
    const newOptions = [...pollForm.options];
    newOptions[index] = value;
    setPollForm({ ...pollForm, options: newOptions });
  };

  const handleCreatePoll = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!pollForm.title.trim()) {
      setError('Titel ist erforderlich');
      return;
    }
    if (pollForm.options.filter((opt) => opt.trim()).length < 2) {
      setError('Mindestens 2 Antwortoptionen sind erforderlich');
      return;
    }
    setCreatingPoll(true);
    setError('');
    try {
      const response = await api.post('/polls', {
        title: pollForm.title.trim(),
        description: pollForm.description.trim() || null,
        options: pollForm.options.filter((opt) => opt.trim()),
        ends_at: pollForm.ends_at || null,
      });
      setPolls((prev) => [response.data.poll, ...prev]);
      setPollForm({ title: '', description: '', options: ['', ''], ends_at: '' });
      setShowPollForm(false);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Umfrage konnte nicht erstellt werden');
    } finally {
      setCreatingPoll(false);
    }
  };

  if (!user) {
    return (
      <div className="devchat-page">
        <div className="devchat-card">
          <h1>Entwickler-Chat</h1>
          <p>Bitte melde dich an, um am Entwickler-Chat teilzunehmen.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="devchat-page">
      <div className="devchat-card">
        <h1>Entwickler-Chat</h1>
        <p className="devchat-subtitle">
          Teile hier deine Wünsche, Ideen und Bugreports direkt mit dem Entwickler-Team.
        </p>

        {user?.is_admin && (
          <div className="devchat-admin-actions">
            <button
              type="button"
              className="devchat-poll-button"
              onClick={() => setShowPollForm(!showPollForm)}
            >
              {showPollForm ? '✕ Abbrechen' : '📊 Neue Umfrage erstellen'}
            </button>
          </div>
        )}

        {showPollForm && user?.is_admin && (
          <form className="devchat-poll-form" onSubmit={handleCreatePoll}>
            <h3>Neue Umfrage erstellen</h3>
            <input
              type="text"
              placeholder="Titel der Umfrage *"
              value={pollForm.title}
              onChange={(e) => setPollForm({ ...pollForm, title: e.target.value })}
              required
              maxLength={200}
            />
            <textarea
              placeholder="Beschreibung (optional)"
              value={pollForm.description}
              onChange={(e) => setPollForm({ ...pollForm, description: e.target.value })}
              rows={2}
              maxLength={500}
            />
            <div className="poll-options-input">
              <label>Antwortoptionen (mindestens 2, maximal 10):</label>
              {pollForm.options.map((option, index) => (
                <div key={index} className="poll-option-input-row">
                  <input
                    type="text"
                    placeholder={`Option ${index + 1} *`}
                    value={option}
                    onChange={(e) => handlePollOptionChange(index, e.target.value)}
                    required
                    maxLength={100}
                  />
                  {pollForm.options.length > 2 && (
                    <button
                      type="button"
                      className="poll-remove-option"
                      onClick={() => handleRemovePollOption(index)}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              {pollForm.options.length < 10 && (
                <button
                  type="button"
                  className="poll-add-option"
                  onClick={handleAddPollOption}
                >
                  + Option hinzufügen
                </button>
              )}
            </div>
            <input
              type="datetime-local"
              placeholder="Enddatum (optional)"
              value={pollForm.ends_at}
              onChange={(e) => setPollForm({ ...pollForm, ends_at: e.target.value })}
            />
            <div className="poll-form-actions">
              <button type="submit" disabled={creatingPoll}>
                {creatingPoll ? 'Wird erstellt...' : 'Umfrage erstellen'}
              </button>
            </div>
          </form>
        )}

        {error && <div className="devchat-error">{error}</div>}

        <form className="devchat-form" onSubmit={handleSubmit}>
          <div className="devchat-input-wrapper">
            <textarea
              value={content}
              onChange={(e) => {
                setContent(e.target.value);
                setError(''); // Fehler beim Tippen löschen
              }}
              placeholder="Dein Vorschlag oder Feedback..."
              rows={3}
              maxLength={2000}
            />
            <button
              type="button"
              className="emoticon-button"
              onClick={(e) => {
                e.preventDefault();
                setShowEmoticonPicker(!showEmoticonPicker);
              }}
              title="Emoticon hinzufügen"
            >
              😊
            </button>
          </div>
          {showEmoticonPicker && (
            <EmoticonPicker
              onSelect={(emoticon) => {
                setContent((prev) => prev + emoticon);
                setShowEmoticonPicker(false);
              }}
              onClose={() => setShowEmoticonPicker(false)}
            />
          )}
          <button type="submit" disabled={sending || !content.trim()}>
            {sending ? 'Wird gesendet...' : 'Beitrag posten'}
          </button>
        </form>

        <div className="devchat-polls">
          {polls.length > 0 && (
            <>
              <h2 className="devchat-section-title">📊 Umfragen</h2>
              {polls.map((poll) => (
                <PollComponent
                  key={poll.id}
                  poll={poll}
                  onVoteUpdate={handlePollVoteUpdate}
                />
              ))}
            </>
          )}
        </div>

        <div className="devchat-messages">
          <h2 className="devchat-section-title">💬 Beiträge</h2>
          {loading ? (
            <p className="devchat-muted">Nachrichten werden geladen...</p>
          ) : messages.length === 0 ? (
            <p className="devchat-muted">Noch keine Beiträge. Sei der Erste!</p>
          ) : (
            <ul>
              {messages.map((msg) => (
                <li key={msg.id} className="devchat-message">
                  <div className="devchat-message-header">
                    <span className="devchat-username">
                      {msg.username ?? `User #${msg.user_id}`}
                    </span>
                    <span className="devchat-date">
                      {new Date(msg.created_at).toLocaleString('de-DE')}
                    </span>
                  </div>
                  <p className="devchat-content">{msg.content}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default DevChat;


